import React, { useEffect, useState } from 'react';
import { BarChart3, Download } from 'lucide-react';
import Card from '../UI/Card';
import Button from '../UI/Button';
import { triageApi } from '../../api/triage';

const pct = (rate) => (rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`);

const Stat = ({ label, value, hint }) => (
  <div>
    <div className="text-xl font-semibold text-gray-100">{value}</div>
    <div className="text-xs text-gray-400">{label}</div>
    {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
  </div>
);

const TriageQualityCard = () => {
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    triageApi
      .getMetrics()
      .then((m) => alive && setMetrics(m))
      .catch((err) => alive && setError(err?.response?.status === 403 ? 'Only administrators can view this.' : 'Could not load triage metrics.'));
    return () => {
      alive = false;
    };
  }, []);

  const handleDownload = async (format) => {
    setBusy(format);
    setDownloadError(null);
    try {
      const blob = await triageApi.download(format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `triage-export.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0); // let the browser start the download first
    } catch {
      setDownloadError('Could not download the export.');
    } finally {
      setBusy(null);
    }
  };

  const a = metrics?.agreement;
  const count = (status) => metrics?.by_status?.[status] ?? 0;
  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-orange-500/70" />
          <h3 className="text-base font-semibold text-gray-100">AI triage quality</h3>
        </div>
        <p className="text-sm text-gray-400 mt-0.5">
          How often analysts agree with the AI verdicts, how many verdicts have feedback, and how fast they arrive.
          Feedback comes from the thumbs on each verdict and from the outcome of closed cases.
        </p>
      </Card.Header>
      <Card.Body className="space-y-4">
        {[error, downloadError].filter(Boolean).map((message) => (
          <div key={message} role="status" aria-live="polite" className="px-4 py-3 rounded-lg border text-sm bg-red-500/10 border-red-500/30 text-red-400">
            {message}
          </div>
        ))}

        {metrics && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Stat
                label="Agreement"
                value={metrics.with_feedback === 0 ? '—' : pct(a.overall.rate)}
                hint={metrics.with_feedback === 0 ? 'No feedback yet' : `explicit ${pct(a.explicit.rate)} · from cases ${pct(a.implicit.rate)}`}
              />
              <Stat label="With feedback" value={pct(metrics.feedback_coverage)} hint={`${metrics.with_feedback} of ${metrics.completed} verdicts`} />
              <Stat
                label="Median time to verdict"
                value={metrics.seconds_to_verdict.median === null ? '—' : `${Math.round(metrics.seconds_to_verdict.median)} s`}
                hint={metrics.seconds_to_verdict.p90 === null ? undefined : `90th percentile ${Math.round(metrics.seconds_to_verdict.p90)} s`}
              />
            </div>
            <p className="text-xs text-gray-500">
              {metrics.completed} completed · {metrics.by_verdict.malicious} malicious · {metrics.by_verdict.suspicious} suspicious · {metrics.by_verdict.benign} benign
              {` · ${count('failed')} failed · ${count('skipped')} skipped · ${count('queued') + count('running')} pending`}
            </p>
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="md" onClick={() => handleDownload('csv')} disabled={busy !== null}>
            <Download className="w-4 h-4 mr-2" />
            Download CSV
          </Button>
          <Button variant="secondary" size="md" onClick={() => handleDownload('json')} disabled={busy !== null}>
            <Download className="w-4 h-4 mr-2" />
            Download JSON
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default TriageQualityCard;
